<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Plus, Delete, Calendar, Clock, Sunny, Star, CopyDocument } from '@element-plus/icons-vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import type { Schedule, OpenHours, SpecialDay, WeeklyHours, DayOfWeek } from '@posx/core';

const { t } = useI18n();
const props = withDefaults(defineProps<{ modelValue?: any; defaultStartTime?: string; defaultEndTime?: string }>(), {
  defaultStartTime: '09:00',
  defaultEndTime: '17:00',
});
const emit = defineEmits<{ 'update:modelValue': [value: Schedule[]] }>();

const L = 'settings.pos.general_settings.opening_schedules';
const tt = (k: string) => t(`${L}.${k}`);
const activeTabs = ref<string[]>([]); // per-schedule active segment
const ORDER: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TABS = [
  { k: 'weekly', icon: Calendar, label: 'tabs.weekly' },
  { k: 'default', icon: Clock, label: 'tabs.default' },
  { k: 'special', icon: Star, label: 'tabs.special' },
  { k: 'holidays', icon: Sunny, label: 'tabs.holidays' },
];
const dayName = (d: DayOfWeek) => tt(`days.${d}`);
const dayAbbr = (d: DayOfWeek) => dayName(d).split(' ').pop()!; // last token: Mon / 周一 / hai (locale-safe distinct label)
const today = () => new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD, avoids UTC off-by-one
const newHour = (): OpenHours => ({ open: props.defaultStartTime, close: props.defaultEndTime });
const isOff = (o: { closed?: boolean; hours?: OpenHours[] }) => !!o.closed || !o.hours?.length;
const summarize = (o: WeeklyHours) => isOff(o) ? tt('closed') : o.hours!.map((h) => `${h.open}–${h.close}`).join(' , ');

// Expand any legacy grouped weekly_schedules into a canonical 7-day grid (one entry per day, Mon→Sun)
const normalizeWeekly = (s: Schedule) => {
  // no-op when already canonical: keeps normalize idempotent so it can't retrigger the deep watch on modelValue below
  if (s.weekly_schedules?.length === ORDER.length && ORDER.every((d, i) => s.weekly_schedules![i].days?.[0] === d)) return;
  const had = !!s.weekly_schedules?.length;
  const map = new Map<DayOfWeek, WeeklyHours>();
  for (const g of s.weekly_schedules ?? []) for (const d of g.days ?? []) map.set(d, { days: [d], closed: g.closed ?? false, hours: (g.hours ?? []).map((h) => ({ ...h })) });
  s.weekly_schedules = ORDER.map((d) => map.get(d) ?? (had ? { days: [d], closed: true, hours: [] } : { days: [d], closed: false, hours: [newHour()] }));
};
const normalize = (s: any): Schedule => {
  s.opening_hours ??= { open: '10:00', close: '22:00' };
  s.special_days ??= [];
  s.holidays ??= [];
  normalizeWeekly(s);
  return s;
};
// modelValue may be Schedule[] or wrapped by SchemaForm; always return a non-empty Schedule[]
const toSchedules = (v: any): Schedule[] => {
  let arr: any[] = [];
  if (Array.isArray(v)) arr = v;
  else if (v?.opening_schedules) return toSchedules(v.opening_schedules);
  else if (v?.['']) return toSchedules(v['']);
  else if (v?.opening_hours) arr = [v];
  return arr.length ? arr.map(normalize) : [normalize({})];
};

const schedules = ref<Schedule[]>(toSchedules(props.modelValue));
let selfEmit = false; // ignore the parent echoing back our own emit (prevents el-time-select update loop)
watch(() => props.modelValue, (v) => { if (selfEmit) { selfEmit = false; return; } schedules.value = toSchedules(v); }, { deep: true });
const emitChange = () => { selfEmit = true; emit('update:modelValue', schedules.value); };
const tabOf = (i: number) => activeTabs.value[i] || 'weekly';
const openCount = (s: Schedule) => s.weekly_schedules!.filter((g) => !isOff(g)).length; // for header summary

// Glance ↔ day-row sync: click jumps to a day, scrolling highlights the day in view
const activeDay = ref<Record<number, number>>({});
const activeOf = (si: number) => activeDay.value[si] ?? 0;
const dayEls: Record<string, HTMLElement> = {};
let io: IntersectionObserver | undefined;
const setDayRef = (si: number, gi: number, el: any) => {
  const k = `${si}-${gi}`;
  if (el) { el.dataset.k = k; dayEls[k] = el; io?.observe(el); }
  else if (dayEls[k]) { io?.unobserve(dayEls[k]); delete dayEls[k]; }
};
const goToDay = (si: number, gi: number) => { activeDay.value[si] = gi; dayEls[`${si}-${gi}`]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); };

const addSchedule = () => { schedules.value.push(normalize({})); emitChange(); };
const removeSchedule = (i: number) => { schedules.value.splice(i, 1); activeTabs.value.splice(i, 1); emitChange(); };

// Generic open/closed + time-slot handlers shared by weekly days and special days
const setOpen = (o: any, open: boolean) => { o.closed = !open; if (open && !o.hours?.length) o.hours = [newHour()]; emitChange(); };
const addSlot = (o: any) => { (o.hours ??= []).push(newHour()); emitChange(); };
const removeSlot = (o: any, i: number) => { o.hours.splice(i, 1); emitChange(); };
const copyToAll = (s: Schedule, src: WeeklyHours) => {
  for (const g of s.weekly_schedules!) if (g !== src) { g.closed = src.closed; g.hours = (src.hours ?? []).map((h) => ({ ...h })); }
  emitChange();
  ElNotification({ customClass: 'x-notification__success', title: tt('copied'), duration: 2500 });
};

const addSpecial = (s: Schedule) => { s.special_days!.push({ date: today(), closed: false, hours: [newHour()] }); emitChange(); };
const removeSpecial = (s: Schedule, i: number) => { s.special_days!.splice(i, 1); emitChange(); };
const addHoliday = (s: Schedule) => { s.holidays!.push({ name: '', date: today() }); emitChange(); };
const removeHoliday = (s: Schedule, i: number) => { s.holidays!.splice(i, 1); emitChange(); };

onMounted(() => {
  io = new IntersectionObserver((es) => { for (const e of es) if (e.isIntersecting) { const [si, gi] = (e.target as HTMLElement).dataset.k!.split('-').map(Number); activeDay.value[si] = gi; } }, { rootMargin: '-45% 0px -50% 0px' });
  for (const k in dayEls) io.observe(dayEls[k]);
});
onBeforeUnmount(() => io?.disconnect());
</script>

<template>
  <div class="osw">
    <div v-for="(s, si) in schedules" :key="si" class="osw-card" :style="{ '--i': si }">
      <!-- Card header: badge · title · open-day count · delete -->
      <header class="osw-head">
        <span class="osw-head__badge"><el-icon><Clock /></el-icon></span>
        <div class="osw-head__text">
          <span class="osw-head__title">{{ tt('schedule') }}<em v-if="schedules.length > 1"> #{{ si + 1 }}</em></span>
          <span class="osw-head__sub">{{ openCount(s) }}/7 {{ tt('open') }}</span>
        </div>
        <button v-if="schedules.length > 1" type="button" class="osw-x" @click="removeSchedule(si)"><el-icon><Delete /></el-icon></button>
      </header>

      <!-- Segmented control -->
      <nav class="osw-seg">
        <button v-for="tab in TABS" :key="tab.k" type="button" class="osw-seg__btn" :class="{ active: tabOf(si) === tab.k }" @click="activeTabs[si] = tab.k">
          <el-icon><component :is="tab.icon" /></el-icon><span>{{ tt(tab.label) }}</span>
        </button>
      </nav>

      <!-- WEEKLY: one row per weekday -->
      <section v-show="tabOf(si) === 'weekly'" class="osw-body">
        <!-- Week-at-a-glance strip: sticky overview of the 7-day grid -->
        <div class="glance">
          <el-tooltip v-for="(g, gi) in s.weekly_schedules" :key="g.days![0]" :content="summarize(g)" placement="top" :show-after="120">
            <div class="glance__day" :class="{ off: isOff(g), active: gi === activeOf(si) }" @click="goToDay(si, gi)">
              <span class="glance__lbl">{{ dayAbbr(g.days![0]) }}</span>
              <span class="glance__dot" />
            </div>
          </el-tooltip>
        </div>
        <div v-for="(g, gi) in s.weekly_schedules" :key="g.days![0]" :ref="(el) => setDayRef(si, gi, el)" class="day" :class="{ 'is-off': g.closed }" :style="{ '--row': gi }">
          <div class="day__bar">
            <button type="button" class="tgl" :class="{ on: !g.closed }" role="switch" :aria-checked="!g.closed" @click="setOpen(g, !!g.closed)">
              <span class="tgl__track"><span class="tgl__thumb" /></span>
            </button>
            <span class="day__name">{{ dayName(g.days![0]) }}</span>
            <span class="day__state">{{ g.closed ? tt('closed') : tt('open') }}</span>
            <el-tooltip :content="tt('copy_to_all')" placement="top" :show-after="160">
              <button type="button" class="iconbtn" @click="copyToAll(s, g)"><el-icon><CopyDocument /></el-icon></button>
            </el-tooltip>
          </div>
          <div v-if="!g.closed" class="day__hours">
            <div v-for="(h, hi) in g.hours" :key="hi" class="range">
              <el-time-select v-model="h.open" start="00:00" step="00:15" end="23:45" :placeholder="tt('open')" class="ts" @change="emitChange" />
              <span class="range__arrow">→</span>
              <el-time-select v-model="h.close" start="00:00" step="00:15" end="23:45" :placeholder="tt('closed')" class="ts" @change="emitChange" />
              <button v-if="g.hours!.length > 1" type="button" class="iconbtn iconbtn--danger" @click="removeSlot(g, hi)"><el-icon><Delete /></el-icon></button>
            </div>
            <button type="button" class="addslot" @click="addSlot(g)"><el-icon><Plus /></el-icon>{{ tt('add_time') }}</button>
          </div>
        </div>
      </section>

      <!-- DEFAULT: single fallback range -->
      <section v-show="tabOf(si) === 'default'" class="osw-body">
        <div class="default-card">
          <span class="default-card__lead"><el-icon><Clock /></el-icon>{{ tt('tabs.default') }}</span>
          <div class="range">
            <el-time-select v-model="s.opening_hours.open" start="00:00" step="00:15" end="23:45" class="ts" @change="emitChange" />
            <span class="range__arrow">→</span>
            <el-time-select v-model="s.opening_hours.close" start="00:00" step="00:15" end="23:45" class="ts" @change="emitChange" />
          </div>
          <p class="default-card__hint">{{ tt('default_hint') }}</p>
        </div>
      </section>

      <!-- SPECIAL DATES -->
      <section v-show="tabOf(si) === 'special'" class="osw-body">
        <div v-if="!s.special_days!.length" class="empty"><el-icon><Star /></el-icon><span>{{ tt('no_special_dates') }}</span></div>
        <div v-for="(sd, sdi) in s.special_days" :key="sdi" class="day" :class="{ 'is-off': sd.closed }">
          <div class="day__bar">
            <button type="button" class="tgl" :class="{ on: !sd.closed }" role="switch" :aria-checked="!sd.closed" @click="setOpen(sd, sd.closed)">
              <span class="tgl__track"><span class="tgl__thumb" /></span>
            </button>
            <el-date-picker v-model="sd.date" type="date" format="YYYY-MM-DD" value-format="YYYY-MM-DD" size="large" @change="emitChange" class="dp" />
            <button type="button" class="iconbtn iconbtn--danger" @click="removeSpecial(s, sdi)"><el-icon><Delete /></el-icon></button>
          </div>
          <div v-if="!sd.closed" class="day__hours">
            <div v-for="(h, hi) in sd.hours" :key="hi" class="range">
              <el-time-select v-model="h.open" start="00:00" step="00:15" end="23:45" class="ts" @change="emitChange" />
              <span class="range__arrow">→</span>
              <el-time-select v-model="h.close" start="00:00" step="00:15" end="23:45" class="ts" @change="emitChange" />
              <button v-if="sd.hours.length > 1" type="button" class="iconbtn iconbtn--danger" @click="removeSlot(sd, hi)"><el-icon><Delete /></el-icon></button>
            </div>
            <button type="button" class="addslot" @click="addSlot(sd)"><el-icon><Plus /></el-icon>{{ tt('add_time') }}</button>
          </div>
        </div>
        <button type="button" class="addrow" @click="addSpecial(s)"><el-icon><Plus /></el-icon>{{ tt('add_special_date') }}</button>
      </section>

      <!-- HOLIDAYS -->
      <section v-show="tabOf(si) === 'holidays'" class="osw-body">
        <div v-if="!s.holidays!.length" class="empty"><el-icon><Sunny /></el-icon><span>{{ tt('no_holidays') }}</span></div>
        <div v-for="(hd, hi) in s.holidays" :key="hi" class="holiday">
          <el-input v-model="hd.name" :placeholder="tt('holiday_name')" size="large" @change="emitChange" class="holiday__name" />
          <el-date-picker v-model="hd.date" type="date" format="YYYY-MM-DD" value-format="YYYY-MM-DD" size="large" @change="emitChange" class="dp" />
          <button type="button" class="iconbtn iconbtn--danger" @click="removeHoliday(s, hi)"><el-icon><Delete /></el-icon></button>
        </div>
        <button type="button" class="addrow" @click="addHoliday(s)"><el-icon><Plus /></el-icon>{{ tt('add_holiday') }}</button>
      </section>
    </div>

    <button type="button" class="osw-add" @click="addSchedule"><el-icon><Plus /></el-icon>{{ tt('schedule') }}</button>
  </div>
</template>

<style scoped>
.osw {
  --accent: var(--el-color-primary, #2563eb);
  --open: #12a150; --open-soft: #e9f9f0;
  --off: #94a3b8; --off-soft: #f1f5f9;
  --ink: #0f1d2e; --muted: #64748b; --line: #e6ebf2;
  --surface: #ffffff; --surface-2: #f6f8fb;
  width: 100%; max-width: 720px; color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* ---- Card ---- */
.osw-card {
  background: var(--surface); border: 1px solid var(--line); border-radius: 20px;
  padding: 20px; margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(15, 29, 46, 0.04), 0 18px 40px -28px rgba(15, 29, 46, 0.28);
  animation: osw-rise 0.4s both; animation-delay: calc(var(--i) * 70ms);
}
@keyframes osw-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

/* ---- Header ---- */
.osw-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.osw-head__badge { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 13px; background: linear-gradient(140deg, color-mix(in srgb, var(--accent) 20%, white), color-mix(in srgb, var(--accent) 8%, white)); color: var(--accent); font-size: 20px; flex: none; }
.osw-head__text { display: flex; flex-direction: column; gap: 2px; }
.osw-head__title { font-weight: 700; font-size: 16px; letter-spacing: -0.015em; }
.osw-head__title em { color: var(--muted); font-style: normal; font-weight: 500; }
.osw-head__sub { font-size: 12.5px; color: var(--muted); font-weight: 600; }
.osw-x { display: grid; place-items: center; width: 44px; height: 44px; margin-left: auto; border: none; border-radius: 12px; background: transparent; color: var(--muted); cursor: pointer; font-size: 18px; transition: all 0.16s; }
.osw-x:hover { background: #fdecec; color: #e5484d; }

/* ---- Week glance strip (sticky atop weekly list) ---- */
.glance { position: sticky; top: 0; z-index: 2; display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; padding: 4px 0 12px; margin-bottom: 4px; background: var(--surface); }
.glance__day { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 9px 2px; border-radius: 11px; background: var(--open-soft); cursor: pointer; transition: transform 0.15s, outline-color 0.15s; outline: 2px solid transparent; outline-offset: -2px; }
.glance__day.off { background: var(--off-soft); }
.glance__day.active { outline-color: var(--accent); }
.glance__day:hover { transform: translateY(-2px); }
.glance__lbl { font-size: 11px; font-weight: 700; color: var(--open); text-transform: capitalize; letter-spacing: -0.01em; }
.glance__day.off .glance__lbl { color: var(--off); }
.glance__dot { width: 7px; height: 7px; border-radius: 50%; background: var(--open); }
.glance__day.off .glance__dot { background: var(--off); }

/* ---- Segmented control ---- */
.osw-seg { display: flex; gap: 4px; padding: 5px; background: var(--surface-2); border: 1px solid var(--line); border-radius: 15px; margin-bottom: 18px; }
.osw-seg__btn {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  min-height: 46px; padding: 0 6px; border: none; border-radius: 11px; cursor: pointer;
  background: transparent; color: var(--muted); font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
  transition: color 0.18s, background 0.18s, box-shadow 0.18s; white-space: nowrap; min-width: 0;
}
.osw-seg__btn .el-icon { font-size: 16px; flex: none; }
.osw-seg__btn span { overflow: hidden; text-overflow: ellipsis; }
.osw-seg__btn:active { transform: scale(0.97); }
.osw-seg__btn.active { background: var(--surface); color: var(--accent); box-shadow: 0 1px 2px rgba(15, 29, 46, 0.07), 0 4px 12px -5px rgba(15, 29, 46, 0.2); }

.osw-body { display: flex; flex-direction: column; gap: 10px; }

/* ---- Day / special row ---- */
.day {
  border: 1px solid var(--line); border-radius: 16px; padding: 6px 16px 14px;
  background: var(--surface); transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
  animation: osw-rise 0.32s both; animation-delay: calc(var(--row, 0) * 35ms);
}
.day:hover { border-color: #d4dde8; }
.day.is-off { background: var(--surface-2); }

.day__bar { display: flex; align-items: center; gap: 13px; min-height: 56px; }
.day__name { display: flex; align-items: center; gap: 8px; font-weight: 650; font-size: 15px; }
.day__state { margin-left: auto; font-size: 13px; font-weight: 600; color: var(--open); }
.day.is-off .day__state { color: var(--off); }

/* ---- Touch toggle switch ---- */
.tgl { flex: none; display: grid; place-items: center; width: 56px; height: 56px; margin: 0 -6px 0 -8px; border: none; background: transparent; cursor: pointer; }
.tgl__track { position: relative; width: 50px; height: 30px; border-radius: 999px; background: #cdd6e2; transition: background 0.2s; }
.tgl.on .tgl__track { background: var(--open); }
.tgl__thumb { position: absolute; top: 3px; left: 3px; width: 24px; height: 24px; border-radius: 50%; background: #fff; box-shadow: 0 2px 5px rgba(15, 29, 46, 0.25); transition: transform 0.2s cubic-bezier(0.4, 1.3, 0.5, 1); }
.tgl.on .tgl__thumb { transform: translateX(20px); }
.tgl:active .tgl__thumb { width: 28px; }

/* ---- Time slots ---- */
.day__hours { display: flex; flex-direction: column; gap: 10px; padding-top: 12px; border-top: 1px dashed var(--line); }
.range { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.range__arrow { color: var(--muted); font-weight: 700; }
.ts { width: 124px !important; }
.ts :deep(.el-input__wrapper) { height: 48px; border-radius: 12px; box-shadow: 0 0 0 1px var(--line) inset; transition: box-shadow 0.16s; }
.ts :deep(.el-input__wrapper:hover) { box-shadow: 0 0 0 1px #b9c5d4 inset; }
.ts :deep(.el-input__wrapper.is-focus) { box-shadow: 0 0 0 2px var(--accent) inset; }
.ts :deep(.el-input__inner) { font-weight: 650; font-size: 15px; }
.dp { flex: 1; min-width: 150px; }
.dp :deep(.el-input__wrapper) { height: 48px; border-radius: 12px; }

/* ---- Icon + add buttons (≥44px touch targets) ---- */
.iconbtn { display: grid; place-items: center; width: 48px; height: 48px; flex: none; border: none; border-radius: 12px; background: var(--surface-2); color: var(--muted); cursor: pointer; transition: all 0.16s; }
.iconbtn:hover { background: #eaeff5; color: var(--ink); }
.iconbtn:active { transform: scale(0.92); }
.iconbtn--danger { background: transparent; }
.iconbtn--danger:hover { background: #fdecec; color: #e5484d; }
.iconbtn .el-icon { font-size: 18px; }

.addslot { display: inline-flex; align-items: center; gap: 6px; align-self: flex-start; height: 42px; padding: 0 16px; border: 1px dashed #c6d1de; border-radius: 11px; background: transparent; color: var(--accent); font-size: 13.5px; font-weight: 650; cursor: pointer; transition: all 0.16s; }
.addslot:hover { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 7%, white); }
.addslot:active { transform: scale(0.98); }

.addrow { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 54px; margin-top: 4px; border: 1.5px dashed #c6d1de; border-radius: 16px; background: transparent; color: var(--accent); font-size: 14.5px; font-weight: 650; cursor: pointer; transition: all 0.18s; }
.addrow:hover { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 6%, white); }
.addrow:active { transform: scale(0.99); }

/* ---- Default card ---- */
.default-card { display: flex; flex-direction: column; gap: 16px; border: 1px solid color-mix(in srgb, var(--open) 22%, white); border-radius: 16px; padding: 22px; background: linear-gradient(180deg, var(--open-soft), #fff); }
.default-card__lead { display: inline-flex; align-items: center; gap: 9px; font-weight: 700; font-size: 14.5px; color: #0c7a3c; }
.default-card__hint { margin: 0; font-size: 13px; color: var(--muted); line-height: 1.55; }

/* ---- Empty state ---- */
.empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 44px; color: var(--muted); font-size: 14px; }
.empty .el-icon { font-size: 34px; opacity: 0.4; }

/* ---- Holiday row ---- */
.holiday { display: flex; align-items: center; gap: 10px; border: 1px solid var(--line); border-radius: 16px; padding: 12px 14px; background: var(--surface); transition: border-color 0.18s; }
.holiday:hover { border-color: #d4dde8; }
.holiday__name { flex: 1; }
.holiday__name :deep(.el-input__wrapper) { height: 48px; border-radius: 12px; }

/* ---- Add schedule ---- */
.osw-add { display: inline-flex; align-items: center; gap: 9px; width: 100%; min-height: 58px; border: 1.5px dashed #c2cfdd; border-radius: 18px; background: var(--surface-2); color: var(--accent); font-size: 15px; font-weight: 700; cursor: pointer; justify-content: center; transition: all 0.18s; }
.osw-add:hover { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 7%, white); transform: translateY(-1px); }
.osw-add:active { transform: translateY(0) scale(0.99); }
.osw-add .el-icon { font-size: 18px; }

/* ---- Narrow / portrait tablet ---- */
@media (max-width: 540px) {
  .osw-seg__btn span { display: none; }
  .osw-seg__btn { min-height: 50px; }
  .ts { flex: 1; min-width: 0; width: auto !important; }
}
</style>
